/** Overlay modals (SCR-05 win, SCR-06 fail/rescue, OVL-01..04, settings, test ad, grand opening, mechanic reminder). */
import type { JSX } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { economy, remoteDefaults, type BoosterKey } from '../config/content.js';
import { app } from '../game/context.js';
import { t } from '../i18n/index.js';
import { checkShopName, suggestShopName } from '../services/names.js';
import { unlockPending } from '../services/progress.js';
import type { PlayerState } from '../services/state.js';
import { viewFor } from '../features/registry.js';
import type { ModalEntry, ModalKind, ModalMap } from './bridge.js';
import { Amount, Button, CountUp, Delayed, GrantView, Modal, Toggle } from './components.js';
import { AdTv, Booster, Calendar, Coin, Flame, Friends, Gem, Gift, Mechanic, PipFace, Shop, Star, Trophy } from './icons.js';
import { Sprite } from './sprite.js';
import { uiSound } from './sound.js';

export interface ModalCtx {
  s: PlayerState;
  /** animations off (tests) → reveal instantly */
  fast: boolean;
  reducedMotion: boolean;
  onSettings: (patch: Partial<PlayerState['settings']>) => void;
  toast: (text: string) => void;
}

type Props<K extends ModalKind> = {
  e: ModalEntry<K> & { props: ModalMap[K]['props'] };
  close: (r: ModalMap[K]['result']) => void;
  c: ModalCtx;
};

// ---------------------------------------------------------------- SCR-05 win

function CoinFly({ n, fast }: { n: number; fast: boolean }): JSX.Element | null {
  if (fast) return null;
  return (
    <div class="coin-fly" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} style={{ animationDelay: `${0.9 + i * 0.07}s`, '--dx': `${(i % 3) * 14 - 14}px` } as JSX.CSSProperties}>
          <Coin size={26} />
        </span>
      ))}
    </div>
  );
}

function WinModal({ e, close, c }: Props<'win'>): JSX.Element {
  const p = e.props;
  const [doubled, setDoubled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [beating, setBeating] = useState(false);
  const tips = doubled ? p.tips * 2 : p.tips;
  useEffect(() => {
    // stars chime in as they pop in (matches the CSS delays)
    const ids = Array.from({ length: p.stars }, (_, i) => setTimeout(() => uiSound('star', i + 1), c.fast ? 0 : 150 + i * 250));
    const coin = setTimeout(() => uiSound('coin'), c.fast ? 0 : 950);
    return () => [...ids, coin].forEach(clearTimeout);
  }, []);
  const total = p.reward + tips + p.sweep;
  const ms = c.fast ? 0 : 700;
  const double = async () => {
    setBusy(true);
    const ok = await p.onDoubleTips();
    setBusy(false);
    if (ok) setDoubled(true);
  };
  const beat = async () => {
    setBeating(true);
    await p.onBeat();
    setBeating(false);
  };
  return (
    <Modal
      title={t('win.title')}
      class="win"
      testId="win-modal"
      hero={
        <div class="win-hero">
          <PipFace size={92} mood="happy" />
          <div class="stars-row" data-testid="win-stars" data-stars={p.stars}>
            {[1, 2, 3].map((i) => (
              <span class={`star-slot s${i} ${i <= p.stars ? 'got' : ''}`} key={i}>
                <Star size={i === 2 ? 64 : 52} empty={i > p.stars} />
              </span>
            ))}
          </div>
        </div>
      }
    >
      <div class="win-shop" aria-hidden="true">
        <Shop size={40} />
      </div>
      {p.newBest && <div class="ribbon">{t('win.newBest')}</div>}
      <div class="reward-rows">
        <div>
          <span>{t('win.reward')}</span>
          <Amount kind="coins" value={p.reward} size={18} />
        </div>
        <div>
          <span>{t('win.tips')}</span>
          <Amount kind="coins" value={tips} size={18} />
        </div>
        {p.sweep > 0 && (
          <div>
            <span>{t('win.sweep')}</span>
            <Amount kind="coins" value={p.sweep} size={18} />
          </div>
        )}
        <div class="total">
          <span class="amount big">
            <Coin size={34} />
            <b data-testid="win-total">
              <CountUp to={total} ms={ms} />
            </b>
          </span>
          <span class="xp">{t('win.xp', { xp: p.xp })}</span>
        </div>
      </div>
      <CoinFly n={8} fast={c.fast || c.reducedMotion} />
      {p.bestCombo > 0 && <p class="muted">{t('win.combo', { n: p.bestCombo + 1 })}</p>}
      {p.finale && <FinaleBanner shop={p.finale} onShop={() => close('shop')} />}
      <div class="actions">
        {p.tips > 0 &&
          (doubled ? (
            <div class="done-chip">{t('win.doubled')}</div>
          ) : p.doubleTips === 'ad' ? (
            <Button variant="ad" onClick={double} disabled={busy || beating} testId="win-double">
              <AdTv /> {t('win.doubleTips')}
            </Button>
          ) : (
            <div class="muted small" data-testid="win-double-later">
              {t('ad.unavailable')}
            </div>
          ))}
        {p.canBeat && (
          <Button variant="teal" onClick={beat} disabled={beating || busy} testId="win-beat">
            {t('beat.cta')}
          </Button>
        )}
        <Delayed ms={c.fast ? 0 : 1200}>
          <Button variant="primary" size="lg" onClick={() => close('continue')} testId="win-continue">
            {t('win.continue')}
          </Button>
        </Delayed>
      </div>
    </Modal>
  );
}

/** Roadmap-3k shop finale: the shop's next upgrade is ready to reveal; the button goes to My shop. */
function FinaleBanner({ shop, onShop }: { shop: string; onShop: () => void }): JSX.Element {
  return (
    <div class="finale-banner" data-testid="win-finale">
      <span class="finale-shop" aria-hidden="true">
        <Shop size={44} />
      </span>
      <b>{t('finale.banner', { shop })}</b>
      <Button variant="teal" size="sm" onClick={onShop} testId="win-finale-shop">
        {t('finale.cta')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------- SCR-06 fail / rescue

function FailModal({ e, close }: Props<'fail'>): JSX.Element {
  const p = e.props;
  return (
    <Modal title={t(`fail.title.${p.reason}`)} class="fail" testId="fail-modal" hero={<PipFace size={92} mood="worried" />}>
      <p class="lead">{t('fail.body')}</p>
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close('retry')} testId="fail-retry">
          {t('fail.retry')}
        </Button>
        {p.canMap && (
          <Button variant="ghost" onClick={() => close('map')} testId="fail-map">
            {t('fail.map')}
          </Button>
        )}
      </div>
    </Modal>
  );
}

/** Feature-launched rounds (tournament, event, duel…): stars or the fail reason, plus the feature's lines. */
function RoundResultModal({ e, close, c }: Props<'roundResult'>): JSX.Element {
  const p = e.props;
  const won = p.result === 'win';
  useEffect(() => {
    if (!won) return;
    const ids = Array.from({ length: p.stars }, (_, i) => setTimeout(() => uiSound('star', i + 1), c.fast ? 0 : 150 + i * 250));
    return () => ids.forEach(clearTimeout);
  }, []);
  return (
    <Modal
      title={p.title ?? (won ? t('win.title') : t(`fail.title.${p.reason ?? 'stuck'}`))}
      class={`round-result ${won ? 'win' : 'fail'}`}
      testId="round-result"
      hero={
        won ? (
          <div class="win-hero">
            <PipFace size={92} mood="happy" />
            <div class="stars-row" data-testid="round-result-stars" data-stars={p.stars}>
              {[1, 2, 3].map((i) => (
                <span class={`star-slot s${i} ${i <= p.stars ? 'got' : ''}`} key={i}>
                  <Star size={i === 2 ? 64 : 52} empty={i > p.stars} />
                </span>
              ))}
            </div>
          </div>
        ) : (
          <PipFace size={92} mood="worried" />
        )
      }
    >
      {(p.lines.length > 0 || p.coins > 0) && (
        <div class="reward-rows" data-testid="round-result-lines">
          {p.lines.map((l, i) => (
            <div key={i} class={l.good ? 'good' : ''} data-testid={l.testId}>
              <span>{l.label}</span>
              <b>{l.value}</b>
            </div>
          ))}
          {p.coins > 0 && (
            <div>
              <span>{t('win.reward')}</span>
              <Amount kind="coins" value={p.coins} size={18} />
            </div>
          )}
        </div>
      )}
      <div class="actions">
        {p.retry && (
          <Button variant="secondary" onClick={() => close('retry')} testId="round-result-retry">
            {t('fail.retry')}
          </Button>
        )}
        <Delayed ms={c.fast ? 0 : 700}>
          <Button variant="primary" size="lg" onClick={() => close('continue')} testId="round-result-continue">
            {t('win.continue')}
          </Button>
        </Delayed>
      </div>
    </Modal>
  );
}

/** What the rescue sheet says is left: the goal's sets; on a collectGoal level, ticket sets and golden items apart. */
function rescueRemaining(p: ModalMap['rescue']['props']): string | null {
  const g = p.goldenLeft ?? 0;
  if (g > 0 && p.setsLeft > 0) return t('rescue.remainingGolden', { n: p.setsLeft, g });
  if (g > 0) return t('rescue.goldenLeft', { n: g });
  return p.setsLeft > 0 ? t('rescue.remaining', { n: p.setsLeft }) : null;
}

function RescueModal({ e, close }: Props<'rescue'>): JSX.Element {
  const p = e.props;
  const left = rescueRemaining(p);
  return (
    <Modal title={t('rescue.title')} class="rescue" testId="rescue-modal" hero={<PipFace size={92} mood="worried" />}>
      <p class="lead">{p.kind === 'time' ? t('rescue.body.time') : t('rescue.body.slot')}</p>
      {left && <p class="muted">{left}</p>}
      <div class="actions">
        {p.adOnly ? (
          <>
            <Button variant="ad" size="lg" onClick={() => close('ad')} disabled={p.ad !== 'ad'} testId="rescue-ad">
              <AdTv /> {p.ad === 'ad' ? t('rescue.ad') : t('ad.unavailable')}
            </Button>
            <p class="muted small">{t('rescue.adOnly')}</p>
          </>
        ) : p.free ? (
          <>
            <Button variant="primary" size="lg" pulse onClick={() => close('free')} testId="rescue-free">
              {t('rescue.free')}
            </Button>
            <p class="muted small">{t('rescue.freeNote')}</p>
          </>
        ) : (
          <>
            <Button variant="ad" size="lg" onClick={() => close('ad')} disabled={p.ad !== 'ad'} testId="rescue-ad">
              <AdTv /> {p.ad === 'ad' ? t('rescue.ad') : t('ad.unavailable')}
            </Button>
            <Button variant="gem" onClick={() => close('gems')} disabled={!p.canAffordGems} testId="rescue-gems">
              <Gem size={22} /> {p.gems}
            </Button>
            {p.boosters > 0 && (
              <Button variant="secondary" onClick={() => close('booster')} testId="rescue-booster">
                <Booster kind="extraSlot" size={24} /> {t('rescue.booster', { n: p.boosters })}
              </Button>
            )}
          </>
        )}
        <Button variant="ghost" size="sm" onClick={() => close('decline')} testId="rescue-decline">
          {t('rescue.decline')}
        </Button>
      </div>
    </Modal>
  );
}

function PauseModal({ e, close, c }: Props<'pause'>): JSX.Element {
  const s = c.s.settings;
  return (
    <Modal title={t('pause.title')} class="pause" testId="pause-modal" onClose={() => close('resume')}>
      <div class="toggles compact">
        <Toggle label={t('settings.sound')} value={s.sound} onChange={(v) => c.onSettings({ sound: v })} />
        <Toggle label={t('settings.music')} value={s.music} onChange={(v) => c.onSettings({ music: v })} />
      </div>
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close('resume')} testId="pause-resume">
          {t('pause.resume')}
        </Button>
        <Button variant="secondary" onClick={() => close('restart')} testId="pause-restart">
          {t('pause.restart')}
        </Button>
        {e.props.canMap && (
          <Button variant="ghost" onClick={() => close('map')} testId="pause-map">
            {t('pause.map')}
          </Button>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Flow A: name + save prompt

function NameShopModal({ close, c }: Props<'nameShop'>): JSX.Element {
  const adjectives = useMemo(() => t('name.adjectives').split(','), []);
  const nouns = useMemo(() => t('name.nouns').split(','), []);
  const compose = (adj: string, noun: string) => t('name.pattern', { adj, noun });
  const [value, setValue] = useState(() => suggestShopName(adjectives, nouns, Math.random, compose));
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const save = () => {
    const r = checkShopName(value);
    if (!r.ok) {
      setError(t(`name.error.${r.reason}`));
      return;
    }
    close(r.name);
  };
  return (
    <Modal title={t('name.title')} class="name" testId="name-modal" hero={<Shop size={72} />}>
      <p class="lead">{t('name.body')}</p>
      <div class="name-row">
        <input
          ref={input}
          value={value}
          maxLength={24}
          aria-label={t('name.placeholder')}
          placeholder={t('name.placeholder')}
          data-testid="name-input"
          onInput={(ev) => {
            setValue((ev.target as HTMLInputElement).value);
            setError(null);
          }}
          onKeyDown={(ev) => ev.key === 'Enter' && save()}
        />
        <button
          type="button"
          class="icon-btn dice"
          aria-label={t('name.shuffle')}
          data-testid="name-shuffle"
          onClick={() => {
            setValue(suggestShopName(adjectives, nouns, Math.random, compose));
            setError(null);
          }}
        >
          🎲
        </button>
      </div>
      {error && <p class="error">{error}</p>}
      <div class="actions">
        <Button variant="primary" size="lg" onClick={save} testId="name-save">
          {t('name.save')}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => close(null)} testId="name-skip">
          {t('name.skip')}
        </Button>
      </div>
      {c.fast && null}
    </Modal>
  );
}

// ---------------------------------------------------------------- Flow D: offline sales, daily gift

function OfflineModal({ e, close }: Props<'offline'>): JSX.Element {
  const p = e.props;
  return (
    <Modal title={t('offline.title')} class="offline" testId="offline-modal" hero={<Shop size={72} />}>
      <p class="lead">{t('offline.body', { hours: p.hours })}</p>
      <div class="big-amount">
        <Amount kind="coins" value={p.coins} size={40} />
      </div>
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close('collect')} testId="offline-collect">
          {t('offline.collect')}
        </Button>
        {p.ad === 'ad' && (
          <Button variant="ad" onClick={() => close('double')} testId="offline-double">
            <AdTv /> {t('offline.double')}
          </Button>
        )}
        {p.ad === 'tryLater' && <p class="muted small">{t('ad.unavailable')}</p>}
      </div>
    </Modal>
  );
}

function DailyModal({ e, close }: Props<'daily'>): JSX.Element {
  const p = e.props;
  return (
    <Modal title={t('daily.title')} class="daily" testId="daily-modal" hero={<Gift size={80} />}>
      <div class="streak">
        <Flame /> {t('daily.streak', { n: p.streak })}
      </div>
      <ol class="days">
        {p.gifts.map((g, i) => (
          <li key={i} class={i + 1 === p.day ? 'today' : i + 1 < p.day ? 'past' : ''}>
            <small>{t('daily.day', { n: i + 1 })}</small>
            <GrantView grant={g} size={16} />
          </li>
        ))}
      </ol>
      <div class="actions">
        <Button variant="primary" size="lg" pulse onClick={() => close('claim')} testId="daily-claim">
          {t('daily.claim')}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- MT-05 level up, GP-07 intros

/** Feature → the view "Try it" opens (OVL-03). */
const TRY: Record<string, string> = { dailyPuzzle: 'dailyPuzzle', league: 'league', friends: 'friends', teams: 'teams', events: 'events' };

/**
 * The flags the unlock intro reads. A modal always renders inside a running app; the bundled
 * defaults answer the unit tests that render one on its own, and those lay the R1 flags over them
 * (test/setup-flags.ts: Teams and Events off), so a test sees the "coming soon" intro.
 */
function liveFlags(): Record<string, boolean> {
  try {
    return app().remote.flags;
  } catch {
    return remoteDefaults.flags;
  }
}

function UnlockIcon({ u }: { u: string }): JSX.Element {
  if (u === 'boosters')
    return (
      <>
        <Booster kind="undo" size={44} />
        <Booster kind="shuffle" size={44} />
      </>
    );
  if (u === 'magnet' || u === 'extraSlot') return <Booster kind={u} size={48} />;
  if (u === 'dailyPuzzle') return <Calendar size={48} />;
  if (u === 'league') return <Trophy size={48} />;
  if (u === 'friends') return <Friends size={48} />;
  return <Star size={44} />;
}

/** MT-05 level up + OVL-03 feature unlock intro (player levels 3/5/8/10/12/15). */
function LevelUpModal({ e, close }: Props<'levelUp'>): JSX.Element {
  const p = e.props;
  const flags = liveFlags();
  const pending = (u: string) => unlockPending(u, flags);
  // "Try it" would open a screen the flag still hides, so it only offers a feature that is open
  const tryIt = p.unlocks
    .filter((u) => !pending(u))
    .map((u) => TRY[u])
    .find((v): v is string => !!v);
  return (
    <Modal title={t('levelup.title', { n: p.level })} class="levelup" testId="levelup-modal" hero={<div class="lvl-burst">{p.level}</div>}>
      {p.unlocks.length === 0 && <p class="lead">{t('levelup.body')}</p>}
      {p.unlocks.map((u) => (
        <div class="unlock" key={u} data-testid={`unlock-${u}`}>
          <div class="unlock-icons">
            <UnlockIcon u={u} />
          </div>
          <b>{t(`unlock.${u}.title`)}</b>
          <p>{t(`unlock.${u}.body`)}</p>
          {pending(u) && (
            <p class="muted small" data-testid={`unlock-soon-${u}`}>
              {t('unlock.soon')}
            </p>
          )}
        </div>
      ))}
      <div class="actions">
        {tryIt && (
          <Button variant="teal" size="lg" onClick={() => close(tryIt)} testId="levelup-try">
            {t('levelup.try')}
          </Button>
        )}
        <Button variant={tryIt ? 'ghost' : 'primary'} size={tryIt ? 'md' : 'lg'} onClick={() => close('ok')} testId="levelup-ok">
          {t('common.great')}
        </Button>
      </div>
    </Modal>
  );
}

/** lockKey: the key badge the board draws on every piece of the key item (teal disc, golden key). */
function KeyBadge({ class: c }: { class?: string }): JSX.Element {
  return (
    <svg class={c} width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10.5" fill="#2ec4b6" stroke="#4a2c2a" stroke-width="1.8" />
      <g transform="rotate(-45 12 12)" stroke="#4a2c2a" stroke-width="1.3" fill="#ffc845" stroke-linejoin="round">
        <path d="M9.6 10.8h8.4v2.4h-1.2v2.2h-1.6v-2.2h-1v1.6h-1.4v-1.6H9.6z" />
        <circle cx="7.4" cy="12" r="3.2" />
        <circle cx="7.4" cy="12" r="1.2" fill="#2ec4b6" />
      </g>
    </svg>
  );
}

/** collectGoal: the gold star a golden tile wears on its corner (thick ink rim: the shape reads without colour). */
function GoldStar({ class: c, size = 22 }: { class?: string; size?: number }): JSX.Element {
  return (
    <svg class={c} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2.2l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.1l-5.9 3.2 1.3-6.6-4.9-4.6 6.6-.8z"
        fill="#ffc400"
        stroke="#4a2c2a"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <ellipse cx="11.4" cy="7.6" rx="1" ry="2" fill="#fff" opacity="0.8" />
    </svg>
  );
}

/** GP-07.1 one-screen intro: the mechanic's real props, one looping animation. */
function MechanicHero({ kind }: { kind: string }): JSX.Element {
  const item = (id: string, cls = '') => <Sprite id={id} size={62} class={cls} />;
  const scenes: Record<string, JSX.Element> = {
    clutter: (
      <div class="mh-row">
        {item('itm_ny_bagel_pretzel')}
        <span class="mh-coin mech-bounce">
          <Coin size={30} />
          <b>+5</b>
        </span>
      </div>
    ),
    wrapped: (
      <div class="mh-stack">
        {item('itm_ny_bagel_donut')}
        <Sprite id="prp_mech_wrap" size={70} class="mh-over mech-lift" />
      </div>
    ),
    twoCustomers: (
      <div class="mh-row">
        <Sprite id="cus_ny_003_happy" size={64} class="mech-bounce" />
        <Sprite id="cus_ny_010_happy" size={64} class="mech-bounce2" />
      </div>
    ),
    rush: <Mechanic kind="rush" size={110} />,
    crates: <Sprite id="prp_mech_crate_3" size={78} class="mech-shake" />,
    ice: (
      <div class="mh-stack">
        {item('itm_ny_bagel_muffin')}
        <Sprite id="prp_mech_ice_2" size={74} class="mh-over mech-fade" />
      </div>
    ),
    glue: (
      <div class="mh-row mh-glue mech-bounce">
        {item('itm_ny_bagel_coffee')}
        {item('itm_ny_bagel_donut')}
        <span class="mh-over mh-tape" />
      </div>
    ),
    bagLock: (
      <div class="mh-row mh-lockbag">
        <span class="mh-stack">
          <Sprite id="ui_play_slot" size={48} />
          {item('itm_ny_bagel_donut', 'mh-over mh-in')}
        </span>
        <span class="mh-stack">
          <Sprite id="ui_play_slot" size={48} />
          <span class="mh-over mh-shutter" />
          <span class="mh-over mh-padlock">
            <i />
          </span>
        </span>
      </div>
    ),
    lockKey: (
      <div class="mh-row mh-lockkey">
        <span class="mh-stack">
          <Sprite id="itm_ny_bagel_coffee" size={50} />
          <KeyBadge class="mh-over mh-key" />
        </span>
        <span class="mh-stack">
          <Sprite id="itm_ny_bagel_donut" size={50} />
          <span class="mh-over mh-padlock">
            <i />
          </span>
        </span>
        <KeyBadge class="mh-over mh-flykey" />
      </div>
    ),
    clearGoal: (
      <div class="mh-closing">
        <span class="mh-ticket">
          <Sprite id="ui_check" size={24} class="mh-over mh-stamp" />
        </span>
        <div class="mh-row mh-shelf">
          <Sprite id="itm_ny_bagel_coffee" size={34} class="mh-gone mh-g1" />
          <Sprite id="itm_ny_bagel_donut" size={34} class="mh-gone mh-g2" />
          <Sprite id="itm_ny_bagel_lox" size={34} class="mh-gone mh-g3" />
        </div>
      </div>
    ),
    collectGoal: (
      <div class="mh-closing mh-golden">
        <span class="mh-ticket">
          <GoldStar class="mh-gstar" />
          <Sprite id="ui_check" size={18} class="mh-over mh-gdone" />
        </span>
        <div class="mh-row mh-shelf">
          <Sprite id="itm_ny_bagel_coffee" size={34} />
          <span class="mh-stack mh-gold">
            <Sprite id="itm_ny_bagel_donut" size={34} />
            <span class="mh-over mh-frame" />
            <GoldStar class="mh-over mh-corner" size={15} />
          </span>
          <Sprite id="itm_ny_bagel_lox" size={34} />
        </div>
      </div>
    ),
    hiddenLine: (
      <div class="mh-mystery">
        <span class="mh-line">
          <Sprite id="itm_ny_bagel_coffee" size={36} />
          <Sprite id="ui_check" size={18} class="mh-over mh-done" />
        </span>
        <span class="mh-line">
          <Sprite id="itm_ny_bagel_donut" size={36} class="mh-face" />
          <span class="mh-over mh-back">
            <b>?</b>
          </span>
          <span class="mh-over mh-n">×3</span>
        </span>
      </div>
    ),
    dispenser: (
      <div class="mh-disp">
        <span class="mh-awning" />
        <span class="mh-window">
          <span class="mh-card" />
          {item('itm_ny_bagel_donut', 'mh-over mh-next')}
        </span>
        <span class="mh-count">×3</span>
      </div>
    ),
    // tray (rules v2): the wicker tray the board draws; its three items lift and pop together, the tray folds
    tray: (
      <div class="mh-traybox">
        <span class="mh-tray" />
        <span class="mh-over mh-titems">
          <Sprite id="itm_ny_bagel_donut" size={36} class="mh-over mh-t1" />
          <Sprite id="itm_ny_bagel_donut" size={36} class="mh-over mh-t2" />
          <Sprite id="itm_ny_bagel_donut" size={36} class="mh-over mh-t3" />
        </span>
        <span class="mh-count mh-tcount">×3</span>
      </div>
    ),
    // threeCustomers (rules v2): three happy customers, one ticket each
    threeCustomers: (
      <div class="mh-row mh-three">
        <Sprite id="cus_ny_003_happy" size={40} class="mech-bounce" />
        <Sprite id="cus_ny_010_happy" size={40} class="mech-bounce2" />
        <Sprite id="cus_ny_017_happy" size={40} class="mech-bounce mh-b3" />
      </div>
    ),
    // shelf (rules v2): the side shelf's row on its plank, four items so the whole row fits the
    // round hero (a reminder card shrinks it further); the two ends stand lifted on a glow, the
    // inner two rest dimmed; the left end leaves and its neighbour rises as the new end
    shelf: (
      <div class="mh-row mh-side">
        <Sprite id="itm_ny_bagel_coffee" size={26} class="mh-send mh-sgo" />
        <Sprite id="itm_ny_bagel_donut" size={26} class="mh-sin mh-snext" />
        <Sprite id="itm_ny_bagel_sesame" size={26} class="mh-sin" />
        <Sprite id="itm_ny_bagel_poppy" size={26} class="mh-send" />
      </div>
    ),
    conveyor: (
      <div class="mh-belt">
        <Sprite id="prp_mech_conveyor" size={120} />
        <div class="mh-over mech-slide mh-row">
          <Sprite id="itm_ny_bagel_coffee" size={40} />
          <Sprite id="itm_ny_bagel_lox" size={40} />
        </div>
      </div>
    ),
  };
  return <div class="mech-hero">{scenes[kind] ?? scenes.clutter}</div>;
}

function MechanicModal({ e, close }: Props<'mechanic'>): JSX.Element {
  const m = e.props.mechanic;
  return (
    <Modal title={t(`mech.${m}.title`)} class="mechanic" testId="mechanic-modal" hero={<MechanicHero kind={m} />}>
      <p class="lead">{t(`mech.${m}.body`)}</p>
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close(undefined)} testId="mechanic-ok">
          {t('mech.ok')}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Roadmap-3k §5: specials the player knows, back for the first time in this city. A lighter
 * GP-07.1: the intro's hero at small size and its one line; several on one level share a card.
 */
function MechanicRemindModal({ e, close }: Props<'mechanicRemind'>): JSX.Element {
  const ms = e.props.mechanics;
  const one = ms.length === 1 ? ms[0]! : null;
  return (
    <Modal
      title={one ? t('mech.remind', { title: t(`mech.${one}.title`) }) : t('mech.remindMany')}
      class="mechanic remind"
      testId="remind-modal"
      hero={one ? <MechanicHero kind={one} /> : undefined}
    >
      {one ? (
        <p class="lead">{t(`mech.${one}.body`)}</p>
      ) : (
        <ul class="remind-list">
          {ms.map((m) => (
            <li key={m} data-testid={`remind-${m}`}>
              <MechanicHero kind={m} />
              <span>
                <b>{t(`mech.${m}.title`)}</b>
                <small>{t(`mech.${m}.body`)}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close(undefined)} testId="remind-ok">
          {t('mech.remindOk')}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- MT-02.3 grand opening

function GrandOpeningModal({ e, close, c }: Props<'grandOpening'>): JSX.Element {
  const p = e.props;
  return (
    <div class="scrim grand" data-testid="grand-modal">
      {!c.reducedMotion && (
        <div class="confetti" aria-hidden="true">
          {Array.from({ length: 36 }, (_, i) => (
            <i key={i} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 12) * 0.12}s`, background: ['#ff6b5b', '#ffc845', '#2ec4b6', '#8e6cef', '#ff70a6'][i % 5] }} />
          ))}
        </div>
      )}
      <div class="panel" role="dialog" aria-label={t('grand.title')}>
        <div class="panel-hero">
          <Shop size={96} />
        </div>
        <h2 class="panel-title">{t('grand.title')}</h2>
        <p class="lead">{t('grand.body', { name: p.shopName })}</p>
        {p.nextShop && <p class="muted">{t('grand.unlocked', { name: p.nextShop })}</p>}
        <div class="actions">
          <Button variant="primary" size="lg" onClick={() => close('share')} testId="grand-share">
            {t('grand.share')}
          </Button>
          <Delayed ms={c.fast ? 0 : 3000}>
            <Button variant="ghost" size="sm" onClick={() => close('close')} testId="grand-skip">
              {t('grand.skip')}
            </Button>
          </Delayed>
        </div>
      </div>
    </div>
  );
}

function BuyBoosterModal({ e, close, c }: Props<'buyBooster'>): JSX.Element {
  const p = e.props;
  const name = t(`booster.${p.booster}`);
  const afford = c.s.gems >= p.price;
  return (
    <Modal title={t('buy.title', { name })} class="buy" testId="buy-modal" hero={<Booster kind={p.booster as BoosterKey} size={72} />} onClose={() => close(false)}>
      <p class="lead">{t(`booster.${p.booster}.desc`)}</p>
      <div class="actions">
        <Button variant="gem" size="lg" onClick={() => close(true)} disabled={!afford} testId="buy-confirm">
          {t('buy.for')} <Gem size={22} /> {p.price}
        </Button>
        {!afford && <p class="muted small">{t('buy.noGems')}</p>}
        {p.ad && (
          <Button variant="ad" onClick={() => close('ad')} testId="buy-ad">
            <AdTv /> {t('buy.ad', { n: p.adQty ?? 1, name })}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => close(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </Modal>
  );
}

function ErrorModal({ e, close }: Props<'error'>): JSX.Element {
  return (
    <Modal title={t('error.title')} class="error-modal" testId="error-modal" hero={<PipFace size={80} mood="sleepy" />}>
      <p class="muted small">{e.props.message}</p>
      <div class="actions">
        <Button variant="primary" size="lg" onClick={() => close('retry')}>
          {t('error.retry')}
        </Button>
      </div>
    </Modal>
  );
}

const REGISTRY: { [K in ModalKind]: (p: Props<K>) => JSX.Element } = {
  win: WinModal,
  fail: FailModal,
  roundResult: RoundResultModal,
  rescue: RescueModal,
  pause: PauseModal,
  nameShop: NameShopModal,
  offline: OfflineModal,
  daily: DailyModal,
  levelUp: LevelUpModal,
  mechanic: MechanicModal,
  mechanicRemind: MechanicRemindModal,
  grandOpening: GrandOpeningModal,
  buyBooster: BuyBoosterModal,
  error: ErrorModal,
};

/** Built-in modal, or a feature view registered at runtime (features/registry.ts). */
export function ModalView(p: { e: ModalEntry; close: (r: unknown) => void; c: ModalCtx }): JSX.Element | null {
  const Comp = ((REGISTRY as Record<string, unknown>)[p.e.kind] ?? viewFor(p.e.kind)) as ((q: typeof p) => JSX.Element | null) | undefined;
  if (!Comp) return null;
  return <Comp {...p} />;
}

export const DAILY_GIFTS = economy.dailyGift;
